// assets/section-product-detail-info.js
/**
 * Product Detail Info Component
 * Handles thumbnail image switching and variant selection
 */

class ProductDetailInfo extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._cache();
    if (this._isReady()) {
      this.addEventListener('click', this._onClick);
      const hasActive = this._thumbnails.some((t) => t.classList.contains('is-active'));
      if (!hasActive && this._thumbnails[0]) this._setActive(this._thumbnails[0]);
    }

    this._initVariantPicker();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _cache() {
    this._thumbnails = Array.from(this.querySelectorAll('.product-detail-info__thumbnail'));
    this._mainImage = this.querySelector('#MainProductImage');
  }

  _isReady() {
    return this._thumbnails.length > 0 && this._mainImage;
  }

  _onClick(event) {
    const thumbnail = event.target.closest('.product-detail-info__thumbnail');
    if (!thumbnail || !this.contains(thumbnail)) return;

    const newImageSrc = thumbnail.dataset.imageSrc;
    if (!newImageSrc) return;

    this._updateActiveState(thumbnail);
    this._updateMainImage(thumbnail, newImageSrc);
  }

  _updateActiveState(activeThumbnail) {
    for (const thumb of this._thumbnails) thumb.classList.remove('is-active');
    this._setActive(activeThumbnail);
  }

  _setActive(thumbnail) {
    thumbnail.classList.add('is-active');
  }

  _updateMainImage(thumbnail, newImageSrc) {
    this._mainImage.src = newImageSrc;
    const thumbnailImg = thumbnail.querySelector('img');
    const nextAlt = thumbnailImg?.alt;
    if (nextAlt) this._mainImage.alt = nextAlt;
  }

  /* ── Variant Picker ── */

  _initVariantPicker() {
    const dataEl = this.querySelector('[data-product-variants]');
    if (!dataEl) return;

    this._variants = JSON.parse(dataEl.textContent);
    this._priceCurrent = this.querySelector('.product-detail-info__price-current');
    this._priceCompare = this.querySelector('.product-detail-info__price-compare');
    this._variantIdInput = this.querySelector('input[name="id"]');
    this._addButton = this.querySelector('button[name="add"]');
    this._moneyFormat = window.theme?.settings?.money_format || '${{amount}}';

    // Event delegation — handles clicks on labels and changes on inputs
    this.addEventListener('change', (e) => {
      if (e.target.matches('.product-detail-info__option-input')) {
        this._onVariantChange();
      }
    });

    // Fallback: direct click on labels (for cases where label→input association fails)
    this.addEventListener('click', (e) => {
      const label = e.target.closest('.product-detail-info__option-value');
      if (!label) return;
      const input = document.getElementById(label.getAttribute('for'));
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  _onVariantChange() {
    const selectedOptions = [];
    this.querySelectorAll('.product-detail-info__option').forEach(fieldset => {
      const checked = fieldset.querySelector('input:checked');
      if (checked) selectedOptions.push(checked.value);
    });

    const variant = this._variants.find(v =>
      selectedOptions.every((opt, i) => v.options[i] === opt)
    );

    // Update active label styling
    this.querySelectorAll('.product-detail-info__option-input').forEach(input => {
      const label = input.nextElementSibling;
      if (label) label.classList.toggle('is-active', input.checked);
    });

    if (!variant) {
      if (this._addButton) {
        this._addButton.setAttribute('disabled', '');
        this._addButton.querySelector('span').textContent =
          window.variantStrings?.unavailable || 'Unavailable';
      }
      return;
    }

    // Update hidden form input
    if (this._variantIdInput) this._variantIdInput.value = variant.id;

    // Update price
    this._updatePrice(variant);

    // Update image
    this._updateVariantImage(variant);

    // Update button state
    this._updateButtonState(variant);
  }

  _updatePrice(variant) {
    if (!this._priceCurrent) return;
    this._priceCurrent.textContent = formatPrice(variant.price, this._moneyFormat);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (!this._priceCompare) {
        const span = document.createElement('span');
        span.className = 'product-detail-info__price-compare';
        this._priceCurrent.after(span);
        this._priceCompare = span;
      }
      this._priceCompare.textContent = formatPrice(variant.compare_at_price, this._moneyFormat);
      this._priceCompare.style.display = '';
    } else if (this._priceCompare) {
      this._priceCompare.style.display = 'none';
    }
  }

  _updateVariantImage(variant) {
    if (!variant.featured_image || !this._mainImage) return;

    // Update main image
    this._mainImage.src = variant.featured_image.src;
    this._mainImage.alt = variant.featured_image.alt || '';

    // Find and activate matching thumbnail
    for (const thumb of this._thumbnails) {
      const thumbSrc = thumb.dataset.imageSrc || '';
      thumb.classList.remove('is-active');
      if (thumbSrc && variant.featured_image.src.includes(
        thumbSrc.replace(/\?.*$/, '').split('/').pop().replace(/(_\d+x\d+|_\d+x)/, '')
      )) {
        thumb.classList.add('is-active');
      }
    }
  }

  _updateButtonState(variant) {
    if (!this._addButton) return;
    const span = this._addButton.querySelector('span');
    if (variant.available) {
      this._addButton.removeAttribute('disabled');
      if (span) span.textContent = this._addButton.dataset.addText || 'Add to Cart';
    } else {
      this._addButton.setAttribute('disabled', '');
      if (span) span.textContent = window.variantStrings?.soldOut || 'Sold Out';
    }
  }
}

customElements.define('product-detail-info', ProductDetailInfo);